import { select } from "~/libs/format";

export function getOfferState (offer, deal) {
  if (deal.status === 'current') {
    return 'funded'
  }

  if (deal.status === 'contractSigned' || deal.status === 'contractIn' || deal.status === 'funding') {
    return 'contractSigned'
  }

  if (deal.status === 'contractRequested' || deal.status === 'contractOut') {
    return 'contractRequested'
  }

  if (offer.accepted) {
    return 'offerAccepted'
  }

  if (deal.autoSubmitType) {
    if (offer.amount) {
      return 'autoOfferDetails'
    } else {
      return 'autoOfferNoDetails'
    }
  }

  if (offer.publishedToBorrower) {
    return 'offerToBorrower'
  } else {
    return 'offerReceived'
  }
}

export function isOfferValidForContractRequest(offer) {
  if (offer.isOfferOption) {
    return true
  }

  if (offer.loanProductType && offer.loanProductType === 'ach') {
    return offer.factor && offer.paymentFrequency && offer.term && offer.points
  }

  return offer.paymentFrequency && offer.term
}

export function normalizeOffer(offer, deal, overrides = {}) {
  return {
    ...offer,
    loanProductCategoryType: offer.loanProductType,
    loanProductLenderId: deal.lenderId,
    originationFeePercent: offer.originationFee,
    factorRate: offer.factor,
    isStaticOffer: true,
    termType: 'months',
    ...overrides,
  }
}

export function getOfferKey(offer) {
  let key = ''
  if (offer.isOfferOption) {
    key = 'option'
  } else if (offer.isStaticOffer) {
    key = 'offer'
  } else {
    key = 'approval'
  }
  return `${key}-${offer.id}`
}

export function getOfferModelType(offer) {
  if (offer.isOfferOption) {
    return  'offerOption'
  } else if (offer.isStaticOffer) {
    return 'offer'
  }

  return 'approval'
}

export function getAcceptedOffer(deal) {
  if (deal?.acceptedOffer) {
    return deal.acceptedOffer
  }
  const activeOffers = (deal?.offers || []).filter((offer) => {
    return Boolean(offer.accepted) || Boolean(offer.borrowerAcceptedDate);
  }).reverse();

  return activeOffers.length ? activeOffers.at(0) : null;
}

export function isMicroSBAProduct(option) {
  return option && option.loanProductId === 1843
}

export function isHELOCProduct(option) {
  return option && option.loanProductId === 1928
}

export function getOverriddenProductName(option) {
  if (isMicroSBAProduct(option)) {
    return 'Micro SBA'
  }
  if (isHELOCProduct(option)) {
    return 'Business purpose HELOC';
  }
  return null
}

export function getLoanProductName(type, name, option = {}, borrowerValues = {}) {
  const overriddenName = getOverriddenProductName(option)
  if (overriddenName) {
    return overriddenName
  }

  const rbfName = borrowerValues?.borrowerState?.value !== 'TX'
    ? 'Revenue-based financing'
    : 'Working capital'

  return {
    ach: rbfName,
    loc: 'Line of credit',
    term: 'Term loan',
    mca: rbfName,
    flexCashflow: rbfName,
  }[type] ?? (name || select(type))
}

export function getLegacyProductName(type, option = {}) {
  const overriddenName = getOverriddenProductName(option)
  if (overriddenName) {
    return overriddenName.toUpperCase()
  }

  return {
    ach: 'WORKING CAPITAL ADVANCE',
    loc: 'LINE OF CREDIT',
    term: 'TERM'
  }[type] ?? select(type)
}

export function visitLender(option) {
  if (!option) {
    return
  }
  const lenderOfferLink = option.offerLink
  if (lenderOfferLink && option.isOfferLinkCheckout) {
    const newTab = window.open('', '_blank')
    if (!newTab) {
      return null
    }
    return {
      success: () => {
        newTab.location.href = lenderOfferLink
        newTab.focus()
      },
      close: () => newTab.close()
    }
  }
  return null
}

export function checkIfOptionMatchesAcceptedOffer(acceptedOffer, option) {
  if (!acceptedOffer || !option) {
    return false
  }
  let dealMatch = false;
  let termMatch = false;
  let amountMatch = false;
  let paymentAmountMatch = true;
  if (acceptedOffer && option.isStaticOffer) {
    dealMatch = option.dealId === acceptedOffer.dealId;
    termMatch = option.term === acceptedOffer.term;
    amountMatch = option.amount === acceptedOffer.amount;

    if (acceptedOffer.paymentFrequency && acceptedOffer.paymentAmount) {
      paymentAmountMatch = acceptedOffer.paymentFrequency === option.paymentFrequency
        && acceptedOffer.paymentAmount === option.paymentAmount
    } else if (acceptedOffer.paymentFrequency) {
      paymentAmountMatch = acceptedOffer.paymentFrequency === option.paymentFrequency
    }
  }
  return dealMatch && termMatch && amountMatch && paymentAmountMatch
}

export default {
  getOfferState,
  isOfferValidForContractRequest,
  normalizeOffer,
  getOfferKey,
  visitLender,
  checkIfOptionMatchesAcceptedOffer
}
